import { useEffect, useRef, useState } from 'react'
import type { MsgDeskPageProps } from '../../MsgDeskApp'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { currentLegacyDeskMode } from '@/msgdeskRuntime/deskNavigation'
import { editorOpenActionForMode } from '../../routing/editor'
import { legacyOperationGate, treeStore } from '@/msgdeskRuntime/state'
import { MsgPage } from './MsgScreen'
import { StdRowSurfaceProvider } from './rowTemplates/RowSurface'
import { StdPanelsSurfaceProvider } from './Page/components/Panels/PanelsSurface'
import { StdSharedSurfaceProvider } from './Shared/SharedSurface'
import { StdEditorSurfaceProvider } from './editor/EditorSurface'
import {
  applyStandardMode,
  isStandardEditorPathname,
  pushStandardEditorUrl,
  pushStandardModeUrl,
  replaceStandardModeUrl,
  standardModeFromPathname,
  type StandardRouteMode,
} from '../../host/deskRoutes'
import { standardEditorModeFromSegment, type StandardEditorMode } from '../../routing/standard'

function routeModeForProjection(mode: string): StandardRouteMode | undefined {
  if (mode === 'msg' || mode === 'answers' || mode === 'submsges' || mode === 'attachments' || mode === 'favs' || mode === 'drafts' || mode === 'trash') return mode
  return undefined
}

export function StdRoutedPage({
  isEditorOpen = false,
  editorMode,
  editorBridgeReady = false,
  initialMode = 'msg',
  initialEditorMode,
}: MsgDeskPageProps & { initialMode?: StandardRouteMode; initialEditorMode?: StandardEditorMode }) {
  const initialModeSettled = useRef(false)
  const initialModeAttempts = useRef(0)
  const initialEditorOpened = useRef(false)
  const editorWasOpen = useRef(false)
  const [initialModeTick, setInitialModeTick] = useState(0)
  const [initialRouteReady, setInitialRouteReady] = useState(initialMode === 'msg')

  useEffect(() => {
    if (initialModeSettled.current || initialMode === 'msg') {
      initialModeSettled.current = true
      setInitialRouteReady(true)
      return
    }

    // S1: the store mode is SEEDED to the route mode for SSR, so it matches
    // before legacy has actually navigated. Settling must wait for the legacy
    // bootstrap (editorBridgeReady) and then consult LEGACY's real desk mode
    // (the projection is held at the route target during adoption). The
    // attachments view is render-only — legacy never enters an 'attachments'
    // mode — so it keeps the store-mode check.
    if (!editorBridgeReady) return

    const currentState = treeStore.getState()
    if (currentState.subsLoading) return
    const matchesRoute = initialMode === 'attachments'
      ? currentState.renderProjection.mode === 'attachments'
      : currentLegacyDeskMode() === initialMode
    const timer = window.setTimeout(() => {
      if (matchesRoute) {
        initialModeSettled.current = true
        setInitialRouteReady(true)
        return
      }
      if (initialModeAttempts.current >= 100) return
      initialModeAttempts.current += 1
      applyStandardMode(initialMode)
      setInitialModeTick(value => value + 1)
    }, matchesRoute ? 300 : 100)

    return () => window.clearTimeout(timer)
  }, [editorBridgeReady, initialMode, initialModeTick])

  useEffect(() => {
    if (!initialEditorMode || !editorBridgeReady || !initialRouteReady || initialEditorOpened.current) return
    initialEditorOpened.current = true
    // EDITOR-ROUTE RACE (audit 2026-07-19 D2, part 2): dispatching at
    // editorBridgeReady lands INSIDE the boot/adoption churn — a transient
    // editor-closed signal then fires the close-effect URL restore and the
    // update-main editor never appears (the async values fetch widens the
    // window; the synchronous new-msg open usually escaped it). Open only
    // once the runtime is idle — the same condition under which the manual
    // sidebar Edit click always worked.
    let cancelled = false
    void legacyOperationGate.whenIdle('std-editor-route:open').then(() => {
      if (!cancelled) dispatchEditorAction({ action: editorOpenActionForMode(initialEditorMode) })
    })
    return () => { cancelled = true }
  }, [editorBridgeReady, initialEditorMode, initialRouteReady])

  useEffect(() => {
    if (isEditorOpen) {
      editorWasOpen.current = true
      if (editorMode === 'new_msg' || editorMode === 'update_msg') pushStandardEditorUrl(editorMode)
      return
    }

    if (editorWasOpen.current && isStandardEditorPathname(window.location.pathname)) {
      editorWasOpen.current = false
      replaceStandardModeUrl('msg')
    }
  }, [editorMode, isEditorOpen])

  useEffect(() => {
    return treeStore.subscribe(() => {
      if (!initialModeSettled.current) return
      // EDITOR-ROUTE RACE (audit 2026-07-19 D2): `initialEditorOpened` flips
      // at DISPATCH time, but the update-main open is async (it fetches the
      // current values first). Transient main-mode projections in that window
      // used to push the mode URL (/codekey), and the router reconciliation
      // remounted the page — killing the opening editor. Hold ALL mode pushes
      // on an editor route until the editor has actually been open once
      // (`editorWasOpen`); after a real open-then-close, the close effect owns
      // the URL restore.
      if (initialEditorMode && !editorWasOpen.current) return
      if (treeStore.getState().renderProjection.mode === 'editor') return
      const mode = routeModeForProjection(treeStore.getState().renderProjection.mode)
      if (mode) pushStandardModeUrl(mode)
    })
  }, [initialEditorMode])

  useEffect(() => {
    const handlePopState = () => {
      const segment = window.location.pathname.split('/').filter(Boolean).pop() || ''
      const routeEditorMode = standardEditorModeFromSegment(segment)
      if (routeEditorMode && editorBridgeReady) {
        dispatchEditorAction({ action: editorOpenActionForMode(routeEditorMode) })
        return
      }
      applyStandardMode(standardModeFromPathname(window.location.pathname))
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [editorBridgeReady])

  return (
    <StdSharedSurfaceProvider>
      <StdRowSurfaceProvider>
        <StdPanelsSurfaceProvider>
          <StdEditorSurfaceProvider>
            <MsgPage isEditorOpen={isEditorOpen} editorMode={editorMode} />
          </StdEditorSurfaceProvider>
        </StdPanelsSurfaceProvider>
      </StdRowSurfaceProvider>
    </StdSharedSurfaceProvider>
  )
}
